import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { components } from "@contracts/generated/editor-service";

import { useValidationStore } from "@/state/validationState";

import { useApiClient } from "./ApiContext";
import { ApiError, unwrap } from "./client";
import { queryKeys } from "./hooks";

export type ProjectSummaryPolicy =
  components["schemas"]["ProjectSummaryPolicy"];
export type ReplaceProjectSummaryPolicyRequest =
  components["schemas"]["ReplaceProjectSummaryPolicyRequest"];
export type SummaryPolicyState = ProjectSummaryPolicy["state"];

/** Read the Project's effective Summary generation policy and its provenance (#864). */
export function useSummaryPolicyQuery(
  projectId: string,
): UseQueryResult<ProjectSummaryPolicy> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.projectSummaryPolicy(projectId),
    async queryFn({ signal }) {
      return unwrap(
        await client.GET("/v1/projects/{projectId}/summary-policy", {
          params: { path: { projectId } },
          signal,
        }),
      );
    },
    enabled: projectId.length > 0,
  });
}

/**
 * Set — or clear — the Project's policy under its compare-and-set identity.
 * The caller supplies the version it actually read; this hook never re-reads a
 * token at submit time, which is the whole point of the CAS.
 */
export function useReplaceSummaryPolicyMutation(
  projectId: string,
): UseMutationResult<
  ProjectSummaryPolicy,
  unknown,
  ReplaceProjectSummaryPolicyRequest
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const clearValidation = useValidationStore((state) => state.clear);

  return useMutation({
    async mutationFn(body) {
      return unwrap(
        await client.PUT("/v1/projects/{projectId}/summary-policy", {
          params: { path: { projectId } },
          body,
        }),
      );
    },
    async onSuccess(saved) {
      queryClient.setQueryData(queryKeys.projectSummaryPolicy(projectId), saved);
      await settleSummaryPolicyWrite(queryClient, projectId, clearValidation);
    },
    onError(error) {
      const failure = classifySummaryPolicyWriteFailure(error);
      // `responseFiltering`'s rule: a stale refusal PROVES the cached token
      // moved, and a transport/5xx failure is ambiguous — the server may have
      // committed before the reply was lost. Refresh in both cases, and never
      // resend automatically.
      if (failure.kind !== "stale" && failure.kind !== "ambiguous") {
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projectSummaryPolicy(projectId),
      });
      void settleSummaryPolicyWrite(queryClient, projectId, clearValidation);
    },
    retry: false,
  });
}

/**
 * What every mounted reader has to re-observe after the policy moved.
 *
 * `responseFiltering.settleResponseFilteringWrite`'s rule, for the same reason
 * and with the same consequence if it is skipped: this write changes what a
 * freeze would export, so a passed validation report certifies content the
 * report never saw and a frozen candidate's server-derived freshness no longer
 * describes the Project. Leaving either cached would keep the candidate gate
 * enabled and keep the Preview modal calling a superseded candidate "Current".
 *
 * The SUMMARY PRESENTATION key is NOT invalidated, and that omission is the
 * point of this ticket rather than an oversight: the report renderer document
 * cannot move because this decision did. The projection keys are not
 * invalidated either — `summary_agent/config.yaml` sits outside the browser's
 * section partition, so no section's fields, locks or provenance can move.
 */
async function settleSummaryPolicyWrite(
  queryClient: QueryClient,
  projectId: string,
  clearValidation: (projectId: string) => void,
): Promise<void> {
  clearValidation(projectId);
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.draft(projectId) }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.projectOverview(projectId),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.previewCandidates(projectId),
    }),
  ]);
}

export type SummaryPolicyWriteFailure =
  | { kind: "stale"; currentPolicyVersion: string | null }
  | { kind: "denied" }
  | { kind: "upstream" }
  | { kind: "invalid"; message: string; requestId: string | undefined }
  | { kind: "ambiguous"; requestId: string | undefined }
  | { kind: "error"; code: string; requestId: string | undefined };

function envelopeString(error: ApiError, key: string): string | null {
  const value = error.envelope[key];
  return typeof value === "string" ? value : null;
}

export function classifySummaryPolicyWriteFailure(
  error: unknown,
): SummaryPolicyWriteFailure {
  if (!(error instanceof ApiError)) {
    return { kind: "ambiguous", requestId: undefined };
  }
  if (error.code === "stale_summary_policy_version") {
    return {
      kind: "stale",
      currentPolicyVersion: envelopeString(error, "currentSummaryPolicyVersion"),
    };
  }
  if (error.isForbidden || error.isUnauthenticated || error.status === 404) {
    return { kind: "denied" };
  }
  if (error.status === 502 || error.status === 503 || error.status === 504) {
    return { kind: "upstream" };
  }
  if (error.status >= 500 && !error.isNotImplemented) {
    return { kind: "ambiguous", requestId: error.requestId };
  }
  if (error.status === 400 || error.status === 413 || error.status === 422) {
    // The SERVER's own sentence, deliberately: a 422 here is the typed
    // validator explaining which rule the submitted state broke, and
    // paraphrasing it would either lose that or duplicate the rules.
    return {
      kind: "invalid",
      message: error.message,
      requestId: error.requestId,
    };
  }
  return { kind: "error", code: error.code, requestId: error.requestId };
}
